/**
 * A centered dialog modal that warns users before opening attachments sent by
 * the other party (renter or landlord). Displays configurable copy, a safety
 * link, and "Go back" / "View attachment" actions. Brand theming is handled
 * automatically via Constellation's theme context.
 *
 * Pair with the `useAttachmentWarning` hook to manage open/dismiss state and
 * session-scoped cookie dismissal.
 */
import {
  Anchor,
  Box,
  Button,
  ButtonGroup,
  Heading,
  Icon,
  Modal,
  Paragraph,
  TextButton,
} from '@zillow/constellation-10';
import { IconWarningFilled } from '@zillow/constellation-icons';

export type AttachmentWarningModalProps = {
  body: string;
  isOpen: boolean;
  linkText: string;
  linkUrl: string;
  onGoBack(): void;
  onViewAttachment(): void;
  title: string;
};

export default function AttachmentWarningModal({
  body,
  isOpen,
  linkText,
  linkUrl,
  onGoBack,
  onViewAttachment,
  title,
}: AttachmentWarningModalProps) {
  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && onGoBack()}
      dividers={false}
      size={{
        base: 'md',
        md: 'sm',
      }}
      body={
        <>
          <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Icon size="xl" css={{ color: 'bg.feedback.warning.hero' }}>
              <IconWarningFilled />
            </Icon>
            <Heading level={3} textStyle="heading-sm" css={{ paddingTop: 'default' }}>
              {title}
            </Heading>
          </Box>
          <Paragraph css={{ paddingTop: 'default' }}>
            {body}{' '}
            <Anchor href={linkUrl} target="_blank" rel="noopener noreferrer">
              {linkText}
            </Anchor>
          </Paragraph>
        </>
      }
      footer={
        <ButtonGroup aria-label="attachment-warning-modal-options">
          <TextButton tone="brand" onClick={onViewAttachment}>
            View attachment
          </TextButton>
          <Button tone="brand" emphasis="filled" onClick={onGoBack}>
            Go back
          </Button>
        </ButtonGroup>
      }
    />
  );
}
